
// @ts-ignore
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useState, useEffect } from 'react';
  import { Button, Card, Form, Alert, Spinner, Modal, Tab, Tabs } from 'react-bootstrap';
import { ChangeEvent } from 'react';
import { FaCopy, FaCheck, FaExternalLinkAlt, FaCode, FaLink, FaInfoCircle } from 'react-icons/fa';
import API from '../../utils/API';
import './NonprofitApiIntegration.css';
// src/main.tsx or src/index.tsx
import 'bootstrap/dist/css/bootstrap.min.css';

interface NonprofitApiIntegrationProps {
  nonprofitId: string;
  nonprofitName?: string;
}

interface ProfileLinkData {
  profileLink: string;
  linkId: string;
  campaignId?: string;
  metadata: {
    nickname?: string;
    hasStripe: boolean;
    hasPaypal: boolean;
    isLive: boolean;
    isApproved: boolean;
  };
}

interface IframeData {
  iframeSrc: string;
  iframeCode: string;
  jsEmbedCode: string;
  configuration: {
    width: string;
    height: string;
    showLogo: boolean;
    campaignId?: string;
    customCss?: string;
  };
}

interface NonprofitInfo {
  id: string;
  name: string;
  nickname?: string;
  campaigns: Array<{
    _id: string;
    name: string;
    description: string;
    goal: number;
    progress: number;
  }>;
  paymentMethods: {
    stripe: boolean;
    paypal: boolean;
  };
  status: {
    isLive: boolean;
    isApproved: boolean;
    stripeComplete: boolean;
  };
}

const NonprofitApiIntegration: React.FC<NonprofitApiIntegrationProps> = ({
  nonprofitId,
  nonprofitName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [nonprofitInfo, setNonprofitInfo] = useState<NonprofitInfo | null>(null);
  const [profileLinkData, setProfileLinkData] = useState<ProfileLinkData | null>(null);
  const [iframeData, setIframeData] = useState<IframeData | null>(null);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  
  // Form states
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [customDomain, setCustomDomain] = useState<string>('');
  const [iframeWidth, setIframeWidth] = useState<string>('100%');
  const [iframeHeight, setIframeHeight] = useState<string>('600px');
  const [showLogo, setShowLogo] = useState<boolean>(true);
  const [customCss, setCustomCss] = useState<string>('');
  
  // Modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewType, setPreviewType] = useState<'link' | 'iframe'>('link');

  useEffect(() => {
    fetchNonprofitInfo();
  }, [nonprofitId]);

  const fetchNonprofitInfo = async () => {
    try {
      setLoading(true);
      const response = await API.getNonprofitInfo(nonprofitId);
      if (response.data.success) {
        setNonprofitInfo(response.data.data);
      }
    } catch (err) {
      setError((err as any).response?.data?.message || 'Failed to fetch nonprofit information');
    } finally {
      setLoading(false);
    }
  };

  const generateProfileLink = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {};
      params.campaignId = nonprofitInfo?.campaigns[0]._id;
      if (customDomain) params.customDomain = customDomain;
      
      const response = await API.generatePublicProfileLink(nonprofitId, params);
      
      if (response.data.success) {
        setProfileLinkData(response.data.data);
        setSuccess('Profile link generated successfully!');
      }
    } catch (err) {
      setError((err as any).response?.data?.message || 'Failed to generate profile link');
    } finally {
      setLoading(false);
    }
  };

  const generateIframeCode = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        width: iframeWidth,
        height: iframeHeight,
        showLogo: showLogo.toString()
      };
      params.campaignId =  nonprofitInfo?.campaigns[0]._id;
      if (customCss) params.customCss = customCss;
      
      const response = await API.generateIframeCode(nonprofitId, params);
      
      if (response.data.success) {
        setIframeData(response.data.data);
        setSuccess('Iframe code generated successfully!');
      }
    } catch (err) {
      setError((err as any).response?.data?.message || 'Failed to generate iframe code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, itemName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const openPreview = (type: 'link' | 'iframe') => {
    setPreviewType(type);
    setShowPreviewModal(true);
  };

  if (loading && !nonprofitInfo) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!nonprofitInfo) {
    return (
      <Alert variant="danger">
        <FaInfoCircle className="me-2" />
        Unable to load nonprofit information. Please try again.
      </Alert>
    );
  }

  return (
    <div className="nonprofit-api-integration">
      <div className="mb-4">
        <h3>API Integration Tools</h3>
        <p className="text-muted">
          Generate public profile links and embed codes for {nonprofitInfo.name}
        </p>
        
        {!nonprofitInfo.status.isApproved && (
          <Alert variant="warning">
            <FaInfoCircle className="me-2" />
            Your nonprofit needs to be approved before using these integration tools.
          </Alert>
        )}
        
        {!nonprofitInfo.status.isLive && (
          <Alert variant="info">
            <FaInfoCircle className="me-2" />
            Your nonprofit is not currently live. Links will work but donations may be limited.
          </Alert>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs  id="api-tabs"
  className="api-tabs mb-4"
  defaultActiveKey="profile-link"
  justify>
        <Tab eventKey="profile-link" title={<><FaLink className="me-2" />Public Profile Link</>}>
          <Card>
            <Card.Body>
              <h5>Generate Public Profile Link</h5>
              <p className="text-muted">
                Create a shareable link that redirects visitors to your Neddie donation page.
              </p>
              
            

                <Button 
                  variant="primary" 
                  onClick={generateProfileLink}
                  disabled={loading || !nonprofitInfo.status.isApproved}
                >
                  {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  Generate Profile Link
                </Button>

              {profileLinkData && (
                <div className="mt-4">
                  <h6>Generated Profile Link</h6>
                  <div className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="text"
                      value={profileLinkData.profileLink}
                      readOnly
                      className="me-2"
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => copyToClipboard(profileLinkData.profileLink, 'profile-link')}
                    >
                      {copiedItem === 'profile-link' ? <FaCheck /> : <FaCopy />}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="ms-2"
                      onClick={() => openPreview('link')}
                    >
                      <FaExternalLinkAlt />
                    </Button>
                  </div>
                   <div className="mt-2">
                    <a
                      href={profileLinkData.profileLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-sm"
                    >
                      <FaExternalLinkAlt className="me-1 ml" />
                      Open Link
                    </a>
                  </div>
                  <small className="text-muted">Link ID: {profileLinkData.linkId}</small>
                 
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="iframe" title={<><FaCode className="me-2" />Iframe Embed</>}>
          <Card>
            <Card.Body>
              <h5>Generate Iframe Embed Code</h5>
              <p className="text-muted">
                Create an iframe that can be embedded on your website to host Neddie's checkout page.
              </p>
              
              <Form>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Width</Form.Label>
                      <Form.Control
                        type="text"
                        value={iframeWidth}
                        onChange={(e) => setIframeWidth(e.target.value)}
                        placeholder="100%"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Height</Form.Label>
                      <Form.Control
                        type="text"
                        value={iframeHeight}
                        onChange={(e) => setIframeHeight(e.target.value)}
                        placeholder="600px"
                      />
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Campaign (Optional)</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedCampaign} 
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedCampaign(e.target.value)}
                  >
                    {/* <option value="">General Donations</option> */}
                    {nonprofitInfo.campaigns.map((campaign) => (
                      <option key={campaign._id} value={campaign._id}>
                        {campaign.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

             

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Neddie Logo"
                    checked={showLogo}
                    onChange={(e) => setShowLogo(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Custom CSS (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={customCss}
                    onChange={(e) => setCustomCss(e.target.value)}
                    placeholder="margin: 10px; border-radius: 12px;"
                  />
                  <Form.Text className="text-muted">
                    Additional CSS styles for the iframe
                  </Form.Text>
                </Form.Group>

                <Button 
                  variant="primary" 
                  onClick={generateIframeCode}
                  disabled={loading || !nonprofitInfo.status.isApproved}
                >
                  {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  Generate Iframe Code
                </Button>
              </Form>

              {iframeData && (
                <div className="mt-4">
                  <Tabs  id="api-tabs"
  className="api-tabs mb-4"
  defaultActiveKey="html"
  justify className="mb-3">
                    <Tab eventKey="html" title="HTML Iframe">
                      <div className="d-flex align-items-center mb-2">
                        <small className="text-muted me-2">HTML Iframe Code:</small>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => copyToClipboard(iframeData.iframeCode, 'iframe-html')}
                        >
                          {copiedItem === 'iframe-html' ? <FaCheck /> : <FaCopy />}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="ms-2"
                          onClick={() => openPreview('iframe')}
                        >
                          <FaExternalLinkAlt />
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        value={iframeData.iframeCode}
                        readOnly
                        className="font-monospace"
                      />
                    </Tab>
                    <Tab eventKey="javascript" title="JavaScript Embed">
                      <div className="d-flex align-items-center mb-2">
                        <small className="text-muted me-2">JavaScript Embed Code:</small>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => copyToClipboard(iframeData.jsEmbedCode, 'iframe-js')}
                        >
                          {copiedItem === 'iframe-js' ? <FaCheck /> : <FaCopy />}
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={12}
                        value={iframeData.jsEmbedCode}
                        readOnly
                        className="font-monospace"
                      />
                    </Tab>
                  </Tabs>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {previewType === 'link' ? 'Profile Link Preview' : 'Iframe Preview'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewType === 'link' && profileLinkData ? (
            <div>
              <p><strong>Link:</strong> {profileLinkData.profileLink}</p>
              <p className="text-muted">
                This link will redirect visitors to your Neddie donation page.
                {profileLinkData.campaignId && ' It will direct them to your selected campaign.'}
              </p>
              <a
                href={profileLinkData.profileLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Open Link <FaExternalLinkAlt className="ms-1" />
              </a>
            </div>
          ) : previewType === 'iframe' && iframeData ? (
            <div>
              <p className="text-muted mb-3">
                This is how the iframe will appear on your website:
              </p>
              <div 
                style={{ 
                  border: '1px solid #dee2e6', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  maxHeight: '400px'
                }}
              >
                <iframe
                  src={iframeData.iframeSrc}
                  width="100%"
                  height="400"
                  frameBorder="0"
                  title="Donation Preview"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NonprofitApiIntegration;
